import { useCallback, useEffect, useRef, useState } from "react";

/**
 * CSS variable the panel writes its width to — the AppShell content margin
 * and the panel itself both read it, so drags update the layout without
 * React re-renders.
 */
export const SIDE_PANEL_WIDTH_VAR = "--ds-side-panel-width";

/** Portal default panel width (createPanelStore DEFAULT_OPTIONS is 500; the DS ships 480). */
export const DEFAULT_SIDE_PANEL_WIDTH = 480;

/** Docked panel may never exceed 70% of the viewport (portal MAX_WIDTH_PERCENT). */
const MAX_WIDTH_PERCENT = 0.7;

const FALLBACK_MIN_WIDTH = 300;

/** Clamp a candidate width into [minWidth, 70vw]. */
export function constrainPanelWidth(
  width: number,
  viewportWidth: number,
  minWidth: number,
): number {
  const maxWidth = viewportWidth * MAX_WIDTH_PERCENT;
  return Math.max(minWidth, Math.min(width, maxWidth));
}

/** Read the --panel-min-width token off :root (Wave 1 shell token, 300px). */
export function readPanelMinWidth(): number {
  const value = getComputedStyle(document.documentElement).getPropertyValue(
    "--panel-min-width",
  );
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : FALLBACK_MIN_WIDTH;
}

export interface UseResizablePanelOptions {
  /** Committed width (state lives in the caller). */
  width: number;
  /** Called once on drag end with the final width. */
  onWidthCommit: (width: number) => void;
}

/**
 * Port of the portal's useResizablePanel without zustand: mousemove deltas
 * are applied to the width CSS variable inside requestAnimationFrame (no
 * re-renders while dragging) and the final width is committed on mouseup.
 * While dragging, the body gets col-resize cursor and selection is off.
 */
export function useResizablePanel({ width, onWidthCommit }: UseResizablePanelOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const rafRef = useRef<number | null>(null);
  const widthRef = useRef(width);
  widthRef.current = width;

  // Publish the committed width whenever it changes outside a drag.
  useEffect(() => {
    document.documentElement.style.setProperty(SIDE_PANEL_WIDTH_VAR, `${width}px`);
  }, [width]);

  const beginDrag = useCallback(() => {
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return;
    }
    const minWidth = readPanelMinWidth();
    const handleMove = (event: MouseEvent) => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        const next = constrainPanelWidth(
          window.innerWidth - event.clientX,
          window.innerWidth,
          minWidth,
        );
        document.documentElement.style.setProperty(SIDE_PANEL_WIDTH_VAR, `${next}px`);
        widthRef.current = next;
      });
    };
    const handleUp = () => {
      setIsDragging(false);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      onWidthCommit(widthRef.current);
    };
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    const { body } = document;
    body.style.userSelect = "none";
    body.style.cursor = "col-resize";
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
      body.style.userSelect = "";
      body.style.cursor = "";
    };
  }, [isDragging, onWidthCommit]);

  return { isDragging, beginDrag };
}
